import React from 'react'
import axios from 'axios'
import i18n from 'i18next'
import Close from '../Icons/Close.jsx'
import Phone from '../Icons/Phone.jsx'
import {getApiUrl} from '../Services/helpers.jsx'
import phoneNumbersAr from '../data/ar/phone-numbers.json'
import phoneNumbersDa from '../data/da/phone-numbers.json'
import phoneNumbersDe from '../data/de/phone-numbers.json'
import phoneNumbersEl from '../data/el/phone-numbers.json'
import phoneNumbersEn from '../data/en/phone-numbers.json'
import phoneNumbersEs from '../data/es/phone-numbers.json'
import phoneNumbersFi from '../data/fi/phone-numbers.json'
import phoneNumbersFr from '../data/fr/phone-numbers.json'
import phoneNumbersHe from '../data/he/phone-numbers.json'
import phoneNumbersHu from '../data/hu/phone-numbers.json'
import phoneNumbersIt from '../data/it/phone-numbers.json'
import phoneNumbersJa from '../data/ja/phone-numbers.json'
import phoneNumbersKo from '../data/ko/phone-numbers.json'
import phoneNumbersNb from '../data/nb/phone-numbers.json'
import phoneNumbersNl from '../data/nl/phone-numbers.json'
import phoneNumbersPl from '../data/pl/phone-numbers.json'
import phoneNumbersPt from '../data/pt/phone-numbers.json'
import phoneNumbersRo from '../data/ro/phone-numbers.json'
import phoneNumbersRu from '../data/ru/phone-numbers.json'
import phoneNumbersSv from '../data/sv/phone-numbers.json'
import phoneNumbersTr from '../data/tr/phone-numbers.json'
import phoneNumbersZh from '../data/zh/phone-numbers.json'

const PHONE_NUMBERS_BY_LOCALE = {
  ar: phoneNumbersAr,
  da: phoneNumbersDa,
  de: phoneNumbersDe,
  el: phoneNumbersEl,
  en: phoneNumbersEn,
  es: phoneNumbersEs,
  fi: phoneNumbersFi,
  fr: phoneNumbersFr,
  he: phoneNumbersHe,
  hu: phoneNumbersHu,
  it: phoneNumbersIt,
  ja: phoneNumbersJa,
  ko: phoneNumbersKo,
  nb: phoneNumbersNb,
  nl: phoneNumbersNl,
  pl: phoneNumbersPl,
  pt: phoneNumbersPt,
  ro: phoneNumbersRo,
  ru: phoneNumbersRu,
  sv: phoneNumbersSv,
  tr: phoneNumbersTr,
  zh: phoneNumbersZh,
}

const chunk = (array, size) => {
  if (!Array.isArray(array) || size <= 0) return []
  const firstChunk = array.slice(0, size)
  if (!firstChunk.length) return array
  if (firstChunk.length === array.length) return [firstChunk]
  return [firstChunk].concat(chunk(array.slice(size, array.length), size))
}

const getPhoneNumbers = () => {
  if (typeof window !== 'undefined' && Array.isArray(window.phoneNumbers)) {
    return window.phoneNumbers
  }
  const lang = (i18n.language || 'en').split('-')[0]
  return PHONE_NUMBERS_BY_LOCALE[lang] || phoneNumbersEn
}

const addPreventScrollUnderModal = () => {
  const body = document.querySelector('body')
  if (body) body.className = `${body.className} is-modal-visible`
}

const removePreventScrollUnderModal = () => {
  const body = document.querySelector('body')
  if (body)
    body.className = `${body.className.replace(/is-modal-visible/g, '')} `
}

const mobileResizeUpdateModalContentHeight = () => {
  if (window.innerWidth <= 768) {
    const modalContent = document.querySelector(
      '.modal .modal__body .modal__content',
    )
    const modalHeader = document.querySelector('.modal .modal__header')
    const modalFooter = document.querySelector('.modal .modal__footer')
    if (modalContent) {
      const modalHeaderHeight = modalHeader?.offsetHeight ?? 0
      const modalFooterHeight = modalFooter?.offsetHeight ?? 0
      const gap = window.innerHeight * 0.15
      const modalContentHeight =
        window.innerHeight - gap - modalHeaderHeight - modalFooterHeight
      modalContent.style = `height: ${modalContentHeight}px;`
    }
  }
}

const FALLBACK_PHONE_NUMBER = '(+44) 3308080544'
const REFRESH_INTERVAL_MS = 60 * 1000

export default class GiveUsACallModal extends React.Component {
  state = {
    isModalVisible: false,
    isOpen: true,
    phoneNumber: FALLBACK_PHONE_NUMBER,
    phoneNumberCountry: '',
    countryISO: '',
  }

  preventDismiss = (event) => {
    event.stopPropagation()
  }

  showModal = (event) => {
    if (event && typeof event.preventDefault === 'function') event.preventDefault()
    this.setState(
      {
        isModalVisible: true,
      },
      () => {
        addPreventScrollUnderModal()
        mobileResizeUpdateModalContentHeight()
      },
    )
  }

  hideModal = () => {
    this.setState(
      {
        isModalVisible: false,
      },
      removePreventScrollUnderModal,
    )
  }

  updateOnlineNow = async () => {
    try {
      const {data: defaults} = await axios.post(getApiUrl() + '/utils/getDefaults')
      this.setState({
        isOpen: !!defaults.open,
        phoneNumber: defaults.phoneNumber || FALLBACK_PHONE_NUMBER,
        phoneNumberCountry: defaults.phoneNumberCountry || '',
        countryISO: defaults.countryISO || '',
      })
    } catch (err) {
      // keep current state on failure
    }
  }

  handleLanguageChanged = () => {
    this.forceUpdate()
  }

  componentDidMount() {
    this.updateOnlineNow()
    this.refreshTimer = setInterval(this.updateOnlineNow, REFRESH_INTERVAL_MS)

    i18n.on('languageChanged', this.handleLanguageChanged)
    if (i18n.isInitialized) {
      this.forceUpdate()
    } else {
      i18n.on('initialized', this.handleLanguageChanged)
    }

    // Backwards-compat: also listen to any pre-existing [data-phone-country]
    // anchors rendered outside the widget (e.g. legacy HUGO shortcode markup).
    this.externalTriggers = document.querySelectorAll(
      '[data-phone-country]:not([data-give-us-a-call-internal])',
    )
    this.externalTriggers.forEach((el) =>
      el.addEventListener('click', this.showModal),
    )
  }

  componentWillUnmount() {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
    i18n.off('languageChanged', this.handleLanguageChanged)
    i18n.off('initialized', this.handleLanguageChanged)
    if (this.externalTriggers) {
      this.externalTriggers.forEach((el) =>
        el.removeEventListener('click', this.showModal),
      )
    }
  }

  render() {
    const {isModalVisible, isOpen, phoneNumber, phoneNumberCountry, countryISO} =
      this.state
    const statusText = isOpen
      ? i18n.t('CallNow.online')
      : i18n.t('CallNow.offline')
    const additionalMsg = isOpen ? '' : i18n.t('CallNow.offline2')

    const localizedKey = phoneNumberCountry
      ? `CallNow.notCallingFrom_${phoneNumberCountry}`
      : ''
    const localizedNotCalling = localizedKey ? i18n.t(localizedKey) : ''
    const messageOtherNumbers =
      phoneNumberCountry &&
      phoneNumberCountry === countryISO &&
      localizedNotCalling &&
      localizedNotCalling !== localizedKey
        ? localizedNotCalling
        : i18n.t('CallNow.notCallingFromFallback')

    const modalPhoneNumbers = getPhoneNumbers()
      .filter((pn) => pn.countryISO === pn.phoneNumberCountry)
      .map((pn) => ({label: pn.countryName, number: pn.phoneNumber}))

    return (
      <>
        <div className={'call-now'}>
          <h5>{i18n.t('CallNow.giveUsACall')}</h5>
          <p
            className={
              isOpen
                ? 'call-now__status call-now__status--active'
                : 'call-now__status'
            }
          >
            {statusText}
          </p>
          <p style={{margin: '10px 0 10px 0'}}>{additionalMsg}</p>
          <p className={'call-now__number reset-dir'}>
            <Phone className={'icon--18 icon--inline icon--dark-grey'} />
            &nbsp;
            <a className={'link link--noline'} href={'tel:' + phoneNumber}>
              {phoneNumber}
            </a>
          </p>
          <a
            className={'link call-now__other'}
            data-phone-country
            data-give-us-a-call-internal
            data-testid={'open-phone-countries-modal'}
            href={'#'}
            onClick={this.showModal}
          >
            {messageOtherNumbers}
          </a>
        </div>

        {isModalVisible && (
          <div className={'modal modal--medium'} onMouseDown={this.hideModal}>
            <div
              role={'dialog'}
              aria-labelledby={'give-us-a-call-modal-title'}
              className={'modal__body'}
              onMouseDown={this.preventDismiss}
            >
              <div className={'modal__content give-us-a-call'}>
                <Close
                  role={'button'}
                  aria-label={'Close modal'}
                  className={'modal__header-close--always-visible'}
                  onMouseDown={this.hideModal}
                />
                <h4 id={'give-us-a-call-modal-title'}>
                  {i18n.t('Components.GiveUsACallModal.title')}
                </h4>
                <div className={'give-us-a-call__numbers-wrapper'}>
                  {chunk(
                    modalPhoneNumbers,
                    Math.max(1, Math.round(modalPhoneNumbers.length / 2)),
                  ).map((phoneNumbersChunk, phoneNumbersChunkIndex) => (
                    <ul
                      className={'give-us-a-call__numbers'}
                      key={phoneNumbersChunkIndex}
                    >
                      {phoneNumbersChunk.map((pn, index) => (
                        <li className={'give-us-a-call__number'} key={index}>
                          <span className={'give-us-a-call__number-label'}>
                            {pn.label}
                          </span>
                          <a
                            className={
                              'link link--noline give-us-a-call__number-number reset-dir'
                            }
                            href={'tel:' + pn.number}
                          >
                            {pn.number}
                          </a>
                        </li>
                      ))}
                    </ul>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    )
  }
}
